<template>
  <div
    class="flex flex-col glass-frosted rounded-8xl w-250 h-67.5 p-4 gap-2"
    :class="{ 'is-animating': isAnimating }"
  >
    <div class="flex items-start justify-between">
      <span class="text-l-medium text-black-white">{{ $t('dpi.slider.title') }}</span>

      <SwitchLabel
        v-model="isIndependent"
        :label="$t('dpi.slider.independentXY')"
        @update:model-value="handleSwitchIndependent"
      />
      <!-- <span class="text-s-medium text-text-glass-default">{{
          
        }}</span> -->
    </div>
    <div class="w-full flex-1 flex flex-col justify-center">
      <div class="w-full flex-1 flex flex-col justify-center gap-2">
        <div :style="getDpiSliderStyle('x')">
          <Slider
            v-model="internalSliders['x']"
            :min="0"
            :max="100"
            :step="0.01"
            thumb-alignment="contain"
            class="h-8 w-full"
            :track-class="`bg-glass30 ${isIndependent ? 'h-6.5' : 'h-8'}`"
            :range-class="`dpi-slider__range ${isIndependent ? 'bg-cyan-default' : 'bg-linear-[var(--color-rt2)]'}`"
            :thumb-class="`bg-transparent rounded-none ${transitionClass} ${isIndependent ? 'h-9.5 w-7' : 'h-10 w-8'}`"
            @keydown.capture="blockSliderKeyboard"
            @value-commit="(val) => handleCommitDPI('x', val)"
          >
            <template #thumb>
              <div class="relative flex items-center justify-center w-full h-full">
                <div class="absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap">
                  <span class="text-m-medium text-brand-blue1 underline">
                    {{ sliderDpis['x'] }}
                  </span>
                </div>

                <Icon
                  :icon="`${isIndependent ? 'Direction-FIll-X-Axis' : 'Direction-FIll-SwipeLR1'}`"
                  :class="`${isIndependent ? 'size-7 text-brand-blue1' : 'size-8'}`"
                />
                <div
                  class="absolute -z-1 w-px bg-black-white left-[50%] top-0.5 -translate-x-1/2"
                  :class="`${isIndependent ? 'h-1.5' : 'h-2'}`"
                />
                <div
                  class="absolute -z-1 w-px bg-black-white left-[50%] bottom-0.5 -translate-x-1/2"
                  :class="`${isIndependent ? 'h-1.5' : 'h-2'}`"
                />
              </div>
            </template>
          </Slider>
        </div>
        <div class="relative w-full h-6">
          <button
            v-for="preset in PRESETS"
            :key="preset"
            class="absolute flex flex-col items-center -translate-x-1/2 group"
            :style="getSliderPositionStyle(dpiToSlider(preset))"
            @click="jumpToPreset(preset)"
          >
            <span
              class="text-m-medium text-text-glass-default group-hover:text-text-hover transition-colors whitespace-nowrap mt-0.5"
            >
              {{ preset }}
            </span>
          </button>
        </div>
        <div v-show="isIndependent" :style="getDpiSliderStyle('y')">
          <Slider
            v-model="internalSliders['y']"
            :min="0"
            :max="100"
            :step="0.01"
            thumb-alignment="contain"
            class="h-8 w-full"
            :track-class="`bg-glass30 ${isIndependent ? 'h-6.5' : 'h-8'}`"
            :range-class="`dpi-slider__range ${isIndependent ? 'bg-red-default' : 'bg-linear-[var(--color-rt2)]'}`"
            :thumb-class="`bg-transparent rounded-none ${transitionClass} ${isIndependent ? 'h-9.5 w-7' : 'h-10 w-8'}`"
            @keydown.capture="blockSliderKeyboard"
            @value-commit="(val) => handleCommitDPI('y', val)"
          >
            <template #thumb>
              <div class="relative flex items-center justify-center w-full h-full">
                <div class="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap">
                  <span class="text-m-medium text-brand-red underline">
                    {{ sliderDpis['y'] }}
                  </span>
                </div>

                <Icon
                  :icon="`${isIndependent ? 'Direction-FIll-Y-Axis' : 'Direction-FIll-SwipeLR1'}`"
                  :class="`${isIndependent ? 'size-7 text-brand-red' : 'size-8'}`"
                />
                <div
                  class="absolute -z-1 w-px bg-black-white left-[50%] top-0.5 -translate-x-1/2"
                  :class="`${isIndependent ? 'h-1.5' : 'h-2'}`"
                />
                <div
                  class="absolute -z-1 w-px bg-black-white left-[50%] bottom-0.5 -translate-x-1/2"
                  :class="`${isIndependent ? 'h-1.5' : 'h-2'}`"
                />
              </div>
            </template>
          </Slider>
        </div>
      </div>
      <div class="flex justify-end gap-2">
        <div
          v-for="axis in activeAxes"
          :key="axis"
          class="p-2 flex items-center justify-center gap-1 bg-key h-12 rounded-6xl"
        >
          <Button
            color="text-b-w"
            variant="frameless"
            shape="circle"
            size="icon-xs"
            @click="() => handleStep(axis, 'decrement')"
          >
            <Icon icon="Direction-FIll-Left3" />
          </Button>
          <div class="flex items-center gap-1">
            <span v-if="isIndependent" class="text-center text-l-medium text-black-white">
              {{ axis.toUpperCase() }}
            </span>
            <input
              v-model="dpiInputs[axis]"
              type="text"
              inputmode="numeric"
              class="no-spinner w-12.5 text-l-medium underline text-center bg-transparent transition-colors outline-none"
              :class="`${isIndependent ? (axis === 'x' ? 'text-brand-blue1 hover:text-blue-dark-hover' : 'text-brand-red hover:text-pink-active') : 'text-black-white hover:text-text-glass-default'}`"
              @input="(e) => handleInput(axis, e)"
              @blur="(e) => commitDpiInput(axis, e)"
              @keydown.enter.prevent="($event.target as HTMLInputElement).blur()"
            />
          </div>
          <Button
            color="text-b-w"
            variant="frameless"
            shape="circle"
            size="icon-xs"
            @click="() => handleStep(axis, 'increment')"
          >
            <Icon icon="Direction-FIll-Right3" />
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { GearType } from '@rapoo/device-sdk'
  import { Slider, Icon, Button, SwitchLabel } from '@rapoo/element'
  import { computed, ref, watch } from 'vue'
  import { useDeviceStore } from '@/stores'
  import { storeToRefs } from 'pinia'

  // ---- 常量 ----
  const DPI_SLIDER_THUMB_WIDTH = 32
  const DPI_INDEPENDENT_SLIDER_THUMB_WIDTH = 28
  const AXES = ['x', 'y'] as const
  const PRESETS = [400, 800, 1200, 1600, 2400, 3200, 6400]
  /** 输入过程允许范围；失焦后再钳到 DPI_MIN–DPI_MAX */
  const INPUT_MIN = 0
  const INPUT_MAX = 99999

  type Axis = (typeof AXES)[number]

  // ---- DPI 配置类型 ----
  interface DpiStepRange {
    from: number
    to: number
    step: number
  }

  interface DpiConfig {
    min: number
    max: number
    /** number: 全局统一步进；number[]: 每段区间的步进值（与 ANCHORS 区间对应） */
    step: number | DpiStepRange[]
  }

  // ---- 默认 fallback 配置（与原来写死的行为一致）----
  const DEFAULT_DPI_CONFIG: DpiConfig = {
    min: 50,
    max: 45000,
    step: [
      { from: 50, to: 10050, step: 10 },
      { from: 10050, to: 45000, step: 50 },
    ],
  }

  // 写死的中间锚点（slider 百分比位置），首尾由 config 动态注入
  const MIDDLE_ANCHORS: [number, number][] = [
    [400, 3.2],
    [800, 6.8],
    [1200, 10.8],
    [1600, 15.3],
    [2400, 23.6],
    [3200, 32.6],
    [6400, 50],
    [10050, 66],
  ]

  const props = defineProps<{
    current?: GearType
    /** 外部同步是否过渡。页面回读期间为 false，用户拖动仍走内部动画。 */
    animate?: boolean
  }>()

  const emits = defineEmits<{
    'independent-change': [val: boolean]
    'dpi-change': [data: { x: number; y: number }]
  }>()

  // ---- Store ----
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)

  // ---- 从 capabilities 中解析 DPI 配置 ----
  const dpiConfig = computed<DpiConfig>(() => {
    const cap = currentDevice.value?.manifest?.capabilities
    if (!cap) return DEFAULT_DPI_CONFIG

    // 查找包含 dpi 字段的 capability 条目
    const dpiCap = cap.find((c: any) => c.type === 'dpi')
    const raw = dpiCap?.config as DpiConfig | undefined
    return raw ?? DEFAULT_DPI_CONFIG
  })

  // ---- 动态 ANCHORS ----
  // 首项取 config.min，尾项取 config.max；中间锚点过滤掉超出范围的
  const anchors = computed<[number, number][]>(() => {
    const { min, max } = dpiConfig.value
    const middle = MIDDLE_ANCHORS.filter(([dpi]) => dpi > min && dpi < max)
    return [[min, 0], ...middle, [max, 100]]
  })

  const DPI_MIN = computed(() => dpiConfig.value.min)
  const DPI_MAX = computed(() => dpiConfig.value.max)

  // ---- 映射函数（使用动态 anchors）----
  function dpiToSlider(dpi: number): number {
    const pts = anchors.value
    for (let i = 1; i < pts.length; i++) {
      const [d0, s0] = pts[i - 1]
      const [d1, s1] = pts[i]
      if (dpi <= d1) {
        return s0 + ((dpi - d0) / (d1 - d0)) * (s1 - s0)
      }
    }
    return 100
  }

  function sliderToDpi(val: number): number {
    const pts = anchors.value
    for (let i = 1; i < pts.length; i++) {
      const [d0, s0] = pts[i - 1]
      const [d1, s1] = pts[i]
      if (val <= s1) {
        return Math.round(d0 + ((val - s0) / (s1 - s0)) * (d1 - d0))
      }
    }
    return DPI_MAX.value
  }

  // ---- snapDpi：兼容 number 和 number[] 两种 step 类型 ----
  function snapDpi(dpi: number): number {
    const { step, min, max } = dpiConfig.value

    let snapped: number

    if (typeof step === 'number') {
      // 全局统一步进
      snapped = Math.round(dpi / step) * step
    } else {
      // 按区间找对应的 step
      const range = step.find((r) => dpi <= r.to) ?? step[step.length - 1]
      snapped = Math.round(dpi / range.step) * range.step
    }

    return Math.min(max, Math.max(min, snapped))
  }

  // ---- 状态 ----
  const isIndependent = ref(false)
  const dpis = ref<Record<Axis, number>>({
    x: 800,
    y: 800,
  })
  const internalSliders = ref<Record<Axis, number[]>>({
    x: [dpiToSlider(dpis.value.x)],
    y: [dpiToSlider(dpis.value.y)],
  })
  const dpiInputs = ref<Record<Axis, string>>({
    x: String(dpis.value.x),
    y: String(dpis.value.y),
  })
  const activeAxes = computed<Axis[]>(() => (isIndependent.value ? [...AXES] : ['x']))
  const sliderDpis = computed<Record<Axis, number>>(() => ({
    x: snapDpi(sliderToDpi(internalSliders.value.x[0] ?? 0)),
    y: snapDpi(sliderToDpi(internalSliders.value.y[0] ?? 0)),
  }))
  const dpiSliderThumbWidth = computed(() =>
    isIndependent.value ? DPI_INDEPENDENT_SLIDER_THUMB_WIDTH : DPI_SLIDER_THUMB_WIDTH,
  )

  // 外部把档位同步进来时，只有页面已经稳定才过渡。首次挂载和回读写终值都不滑。
  const isAnimating = ref(false)
  const transitionClass = computed(() => (isAnimating.value ? 'transition-all duration-300' : ''))

  function shouldAnimateGearChange(current: GearType, previous?: GearType) {
    if (!props.animate || !previous) return false
    return (
      previous.id !== current.id ||
      previous.x !== current.x ||
      previous.y !== current.y ||
      previous.independentXY !== current.independentXY
    )
  }

  // ---- 监听 props.current ----
  watch(
    () => props.current,
    (current, previous) => {
      if (!current) {
        isAnimating.value = false
        return
      }

      if (!props.animate) {
        // 回读写入和首次挂载：同一帧去掉过渡再改位置。
        isAnimating.value = false
      } else if (shouldAnimateGearChange(current, previous)) {
        isAnimating.value = true
        setTimeout(() => {
          isAnimating.value = false
        }, 300)
      }

      isIndependent.value = current.independentXY
      dpis.value.x = current.x
      dpis.value.y = current.y
      dpiInputs.value.x = String(current.x)
      dpiInputs.value.y = String(current.y)
      internalSliders.value.x = [dpiToSlider(current.x)]
      internalSliders.value.y = [dpiToSlider(current.y)]
    },
    { immediate: true },
  )

  // ---- 其余方法（与原来保持一致）----
  function getDpiSliderStyle(axis: Axis) {
    const progress = Math.min(100, Math.max(0, internalSliders.value[axis][0] ?? 0))
    const rangeExtend = dpiSliderThumbWidth.value * (1 - progress / 100)
    return {
      '--dpi-slider-range-right': `calc(${100 - progress}% - ${rangeExtend}px)`,
    }
  }

  function getSliderPositionStyle(progress: number) {
    const clampedProgress = Math.min(100, Math.max(0, progress))
    const thumbOffset = (dpiSliderThumbWidth.value / 2) * (1 - clampedProgress / 50)
    return {
      left: `calc(${clampedProgress}% + ${thumbOffset}px)`,
    }
  }

  const setAxisDpi = (axis: Axis, nextDpi: number) => {
    dpis.value[axis] = snapDpi(nextDpi)
    dpiInputs.value[axis] = String(dpis.value[axis])
    internalSliders.value[axis] = [dpiToSlider(dpis.value[axis])]
  }

  const applyDpi = (axis: Axis, nextDpi: number) => {
    isAnimating.value = true
    setAxisDpi(axis, nextDpi)

    if (!isIndependent.value && axis === 'x') {
      setAxisDpi('y', dpis.value.x)
    }

    emits('dpi-change', { x: dpis.value.x, y: dpis.value.y })
    setTimeout(() => {
      isAnimating.value = false
    }, 300)
  }

  const SLIDER_NAV_KEYS = new Set([
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
    'PageUp',
    'PageDown',
  ])

  /** 拦截 Slider 自带的方向键/Home/End 步进，避免点击后键盘改值 */
  function blockSliderKeyboard(event: KeyboardEvent) {
    if (!SLIDER_NAV_KEYS.has(event.key)) return
    event.preventDefault()
    event.stopImmediatePropagation()
  }

  function handleCommitDPI(axis: Axis, [val]: number[]) {
    applyDpi(axis, sliderToDpi(val))
  }

  /** input 事件过滤：剔除所有非数字字符后钳到 INPUT_MIN–INPUT_MAX */
  const handleInput = (axis: Axis, e: Event) => {
    const input = e.target as HTMLInputElement
    let next = input.value.replace(/\D/g, '')

    if (next !== '') {
      next = String(Math.min(INPUT_MAX, Math.max(INPUT_MIN, Number.parseInt(next, 10))))
    }

    if (next !== input.value) {
      input.value = next
    }
    dpiInputs.value[axis] = next
  }

  // 失焦后钳到 DPI_MIN–DPI_MAX（经 snapDpi）
  const commitDpiInput = (axis: Axis, e: Event) => {
    const raw = dpiInputs.value[axis]
    const parsed = Number.parseInt(raw, 10)

    if (raw === '' || Number.isNaN(parsed)) {
      dpiInputs.value[axis] = String(dpis.value[axis])
      return
    }

    applyDpi(axis, parsed)
    ;(e.target as HTMLInputElement).blur()
  }

  function jumpToPreset(preset: number) {
    // 始终同步更新 x/y：联动模式下若只改 x，打开独立 XY 时 y 会残留旧值
    isAnimating.value = true
    AXES.forEach((axis) => setAxisDpi(axis, preset))

    emits('dpi-change', { x: dpis.value.x, y: dpis.value.y })
    setTimeout(() => {
      isAnimating.value = false
    }, 300)
  }

  const handleStep = (axis: Axis, direction: 'increment' | 'decrement') => {
    const { step } = dpiConfig.value
    let stepVal: number

    if (typeof step === 'number') {
      stepVal = step
    } else {
      const range = step.find((r) => dpis.value[axis] <= r.to) ?? step[step.length - 1]
      stepVal = range.step
    }

    const next = dpis.value[axis] + (direction === 'increment' ? stepVal : -stepVal)
    applyDpi(axis, Math.min(DPI_MAX.value, Math.max(DPI_MIN.value, next)))
  }

  const handleSwitchIndependent = (val: boolean) => {
    if (!val) {
      dpis.value.y = dpis.value.x
      dpiInputs.value.y = String(dpis.value.y)
      internalSliders.value.y = [dpiToSlider(dpis.value.y)]
    }
    emits('independent-change', val)
  }
</script>

<style scoped>
  .no-spinner::-webkit-outer-spin-button,
  .no-spinner::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .no-spinner {
    appearance: textfield;
    -moz-appearance: textfield;
  }

  :deep(.dpi-slider__range) {
    right: var(--dpi-slider-range-right) !important;
  }

  :deep(.dpi-slider__range.animating),
  :deep(.dpi-slider__thumb.animating) {
    transition:
      right 300ms ease,
      left 300ms ease,
      width 300ms ease;
  }

  .is-animating :deep(.dpi-slider__range) {
    transition: right 300ms ease;
  }
</style>
